import { IChannelAvailability, ISection } from 'src/types/menu.types';

type TSectionAvailability = Pick<ISection, 'instanceIds'>;
type TSectionAvailabilityWithChannels = Pick<ISection, 'instanceIds' | 'channels'>;

export const isSectionRestricted = (section: TSectionAvailability): boolean => Array.isArray(section.instanceIds);

export const getEffectiveInstanceIds = (
  section: TSectionAvailability,
  parentSection?: TSectionAvailability,
): string[] | null => {
  const parentInstanceIds = isSectionRestricted(parentSection ?? {}) ? parentSection.instanceIds : null;
  if (!isSectionRestricted(section)) return parentInstanceIds;
  if (!parentInstanceIds) return section.instanceIds;
  return section.instanceIds.filter((id) => parentInstanceIds.includes(id));
};

export const hasNoAvailability = (section: TSectionAvailability, parentSection?: TSectionAvailability): boolean =>
  isSectionRestricted(section) && getEffectiveInstanceIds(section, parentSection)?.length === 0;

export const getEffectiveChannels = (
  section: TSectionAvailabilityWithChannels,
  parentSection?: TSectionAvailabilityWithChannels,
): IChannelAvailability[] => {
  if (!isSectionRestricted(section)) return [];
  const effectiveInstanceIds = getEffectiveInstanceIds(section, parentSection) ?? [];
  return (section.channels ?? []).filter((channel) => effectiveInstanceIds.includes(channel.externalSystemId));
};

export interface IResolvedAvailability {
  instanceIds: string[] | null;
  channels: IChannelAvailability[];
}

/**
 * A section's own effective availability, resolved by walking the whole ancestor
 * chain (not just the immediate parent) — a subsection that itself has no restriction
 * inherits whatever its parent resolves to, which may in turn be inherited from a
 * grandparent, and so on.
 */
export const resolveSectionAvailability = (section: ISection, sections: ISection[]): IResolvedAvailability => {
  const parent = section.parentSectionId
    ? sections.find((candidate) => candidate.sectionId === section.parentSectionId)
    : undefined;
  const parentResolved: IResolvedAvailability = parent
    ? resolveSectionAvailability(parent, sections)
    : { instanceIds: null, channels: [] };

  if (!isSectionRestricted(section)) return parentResolved;

  return {
    instanceIds: getEffectiveInstanceIds(section, { instanceIds: parentResolved.instanceIds }) ?? [],
    channels: getEffectiveChannels(section, parentResolved),
  };
};
